import React from "react";
import styled, { keyframes } from "styled-components";
import fotoPublicidad from "../../images/kusina-foto-restaurante-publicidad.jpg";
import logoInnovasuyu from "../../images/Fondo Innovasuyu.jpg";
import logoProInnovate from "../../images/ProInnovate.png";
import camaraComercioLogo from "../../images/camara-comercio-ica.webp";
import fotoEmpleado1 from "../../images/empleado-kusina-1.jpg";
import fotoEmpleado2 from "../../images/empleado-kusina-2.jpg";
import fotoRepartidor from "../../images/kusina-repartidor-1.jpg";

/* ─── Colores ─── */
const BRAND = "#1410DF";
const ACCENT = "#CC11D2";
const WA_NUMBER = "51973834813";
const WA_MSG = encodeURIComponent("Hola Kusina, quiero saber más sobre cómo unirme 🍽️");
const WA_LINK = `https://wa.me/${WA_NUMBER}?text=${WA_MSG}`;

/* ─── Animaciones ─── */
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ─── Utilidades ─── */
const Section = styled.section`
  padding: 72px 20px;
  @media (max-width: 768px) { padding: 56px 20px; }
`;

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const Tag = styled.span`
  display: inline-block;
  background: ${BRAND}18;
  color: ${BRAND};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  padding: 4px 12px;
  border-radius: 20px;
  margin-bottom: 16px;
`;

const SectionHeading = styled.h2`
  font-size: 28px;
  font-weight: 800;
  color: #111827;
  margin-bottom: 16px;
  line-height: 1.25;
  @media (max-width: 768px) { font-size: 22px; }
`;

const Lead = styled.p`
  font-size: 16px;
  color: #4b5563;
  line-height: 1.75;
  max-width: 640px;
  @media (max-width: 768px) { font-size: 15px; }
`;

const WaButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: #25D366;
  color: white;
  font-weight: 700;
  font-size: 16px;
  padding: 14px 28px;
  border-radius: 50px;
  text-decoration: none;
  box-shadow: 0 4px 20px #25D36640;
  transition: transform 0.18s, box-shadow 0.18s;
  &:hover { transform: translateY(-2px); box-shadow: 0 8px 28px #25D36660; }
  @media (max-width: 768px) { font-size: 15px; padding: 13px 22px; }
`;

const WaIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.115.549 4.1 1.51 5.833L0 24l6.335-1.49A11.945 11.945 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 0 1-5.006-1.373l-.36-.214-3.727.977.994-3.634-.235-.374A9.818 9.818 0 1 1 12 21.818z"/>
  </svg>
);

/* ══════════════════════════════════════════
   HERO
══════════════════════════════════════════ */
const HeroWrapper = styled.section`
  background: linear-gradient(160deg, ${BRAND} 0%, #0a09a0 100%);
  color: white;
  padding: 80px 20px 0;
  overflow: hidden;
  position: relative;
  @media (max-width: 768px) { padding: 60px 20px 0; }
`;

const HeroInner = styled.div`
  max-width: 900px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  align-items: center;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const HeroText = styled.div`
  animation: ${fadeUp} 0.7s ease both;
`;

const HeroTitle = styled.h1`
  font-size: 38px;
  font-weight: 900;
  line-height: 1.15;
  margin-bottom: 18px;
  @media (max-width: 768px) { font-size: 28px; }
`;

const HeroAccent = styled.span`
  background: linear-gradient(90deg, #FFD94A 0%, ${ACCENT} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const HeroSub = styled.p`
  font-size: 17px;
  opacity: 0.88;
  line-height: 1.65;
  margin-bottom: 32px;
  @media (max-width: 768px) { font-size: 15px; }
`;

const HeroVideoBox = styled.div`
  display: flex;
  justify-content: center;
  animation: ${fadeUp} 0.9s ease 0.15s both;
`;

const VideoFrame = styled.iframe`
  width: 220px;
  aspect-ratio: 9/16;
  border-radius: 20px;
  border: none;
  display: block;
  @media (max-width: 768px) { width: 160px; }
`;


/* ══════════════════════════════════════════
   ¿QUÉ ES KUSINA?
══════════════════════════════════════════ */
const QueSomos = styled(Section)`
  background: #fff;
`;

const QueGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 56px;
  align-items: center;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;


const FounderImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border-radius: 20px;
`;

const StatRow = styled.div`
  display: flex;
  gap: 32px;
  margin-top: 28px;
  flex-wrap: wrap;
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatNum = styled.span`
  font-size: 28px;
  font-weight: 900;
  color: ${({ accent }) => accent ? ACCENT : BRAND};
`;

const StatLabel = styled.span`
  font-size: 13px;
  color: #6b7280;
  margin-top: 2px;
`;

/* ══════════════════════════════════════════
   CÓMO FUNCIONA
══════════════════════════════════════════ */
const ComoFunciona = styled(Section)`
  background: #f9fafb;
`;

const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-top: 40px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 36px;
    left: calc(16.66% + 16px);
    right: calc(16.66% + 16px);
    height: 2px;
    background: ${BRAND}30;
    z-index: 0;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    &::before { display: none; }
  }
`;

const StepCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 28px 24px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.07);
  position: relative;
  z-index: 1;
  transition: box-shadow 0.2s;
  &:hover { box-shadow: 0 6px 24px rgba(0,0,0,0.1); }
`;

const StepNum = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${({ accent }) => accent ? ACCENT : BRAND};
  color: white;
  font-size: 20px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
`;

const StepIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
`;

const StepTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 8px;
`;

const StepText = styled.p`
  font-size: 14px;
  color: #4b5563;
  line-height: 1.65;
`;

const NoteStrip = styled.div`
  margin-top: 32px;
  background: ${BRAND}0f;
  border-left: 4px solid ${BRAND};
  border-radius: 0 12px 12px 0;
  padding: 16px 20px;
  font-size: 14px;
  color: #374151;
  line-height: 1.6;
`;

/* ══════════════════════════════════════════
   ¿POR QUÉ KUSINA?
══════════════════════════════════════════ */
const PorQue = styled(Section)`
  background: white;
`;

const CompareGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-top: 36px;
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

const CompareCard = styled.div`
  border-radius: 16px;
  padding: 28px 24px;
  background: ${({ good }) => good ? `${BRAND}08` : "#fef2f2"};
  border: 1.5px solid ${({ good }) => good ? `${BRAND}30` : "#fecaca"};
`;

const CompareLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${({ good }) => good ? BRAND : "#dc2626"};
  margin-bottom: 16px;
`;

const CompareItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 12px;
  font-size: 14px;
  color: #374151;
  line-height: 1.55;

  &::before {
    content: '${({ good }) => good ? "✓" : "✗"}';
    color: ${({ good }) => good ? "#16a34a" : "#dc2626"};
    font-weight: 800;
    font-size: 15px;
    flex-shrink: 0;
    margin-top: 1px;
  }
`;


/* ══════════════════════════════════════════
   COADMINISTRACIÓN
══════════════════════════════════════════ */
const CoadminSection = styled(Section)`
  background: #f9fafb;
`;

const CoadminGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 56px;
  align-items: center;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 28px;
  }
`;

const CoadminVideo = styled.iframe`
  width: 100%;
  aspect-ratio: 9/16;
  border-radius: 20px;
  border: none;
  display: block;
  box-shadow: 0 8px 32px rgba(0,0,0,0.12);
`;

const BulletList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 20px 0 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const BulletItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 15px;
  color: #374151;
  line-height: 1.55;

  &::before {
    content: '✓';
    color: ${ACCENT};
    font-weight: 800;
    font-size: 15px;
    flex-shrink: 0;
    margin-top: 1px;
  }
`;

/* ══════════════════════════════════════════
   POOLER
══════════════════════════════════════════ */
const PoolerSection = styled(Section)`
  background: linear-gradient(135deg, ${BRAND} 0%, #0a09a0 100%);
  color: white;
`;

const PoolerInner = styled(Container)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  align-items: center;
  @media (max-width: 768px) { grid-template-columns: 1fr; gap: 28px; }
`;

const PoolerBenefits = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin-top: 24px;
`;

const PoolerBenefit = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
`;

const BenefitIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(255,255,255,0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
`;

const BenefitText = styled.div``;
const BenefitTitle = styled.div`font-weight: 700; font-size: 15px; margin-bottom: 2px;`;
const BenefitDesc = styled.div`font-size: 13px; opacity: 0.8; line-height: 1.5;`;

const PoolerQuote = styled.div`
  background: rgba(255,255,255,0.1);
  border-radius: 16px;
  padding: 32px;
  font-size: 18px;
  font-style: italic;
  line-height: 1.65;
  border-left: 4px solid #FFD94A;
  @media (max-width: 768px) { font-size: 16px; padding: 24px; }
`;

/* ══════════════════════════════════════════
   TESTIMONIO
══════════════════════════════════════════ */
const TestimonioSection = styled(Section)`
  background: #f9fafb;
`;

const TestimonioCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  max-width: 640px;
  margin: 32px auto 0;
  text-align: center;
`;

const Quote = styled.p`
  font-size: 18px;
  color: #111827;
  font-style: italic;
  line-height: 1.7;
  margin-bottom: 20px;
  @media (max-width: 768px) { font-size: 16px; }
`;

const QuoteAuthor = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${BRAND};
`;

const QuoteRole = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-top: 2px;
`;

const StatBanner = styled.div`
  background: ${BRAND};
  color: white;
  border-radius: 16px;
  padding: 28px 36px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 32px;
  font-size: 18px;
  font-weight: 700;
  text-align: center;
  @media (max-width: 768px) { font-size: 15px; padding: 20px; }
`;

/* ══════════════════════════════════════════
   MISIÓN
══════════════════════════════════════════ */
const MisionSection = styled.section`
  background: linear-gradient(135deg, ${BRAND} 0%, ${ACCENT} 100%);
  color: white;
  padding: 80px 20px;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.04'%3E%3Ccircle cx='30' cy='30' r='20'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  }

  @media (max-width: 768px) { padding: 60px 20px; }
`;

const MisionInner = styled(Container)`
  position: relative;
  z-index: 1;
`;

const MisionEyebrow = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  opacity: 0.75;
  margin-bottom: 20px;
`;

const MisionQuote = styled.blockquote`
  font-size: 32px;
  font-weight: 900;
  line-height: 1.3;
  margin: 0 auto 24px;
  max-width: 700px;
  quotes: none;

  em {
    font-style: normal;
    color: #FFD94A;
  }

  @media (max-width: 768px) { font-size: 22px; }
`;

const MisionDesc = styled.p`
  font-size: 16px;
  opacity: 0.82;
  max-width: 520px;
  margin: 0 auto;
  line-height: 1.7;
  @media (max-width: 768px) { font-size: 14px; }
`;

/* ══════════════════════════════════════════
   NOSOTROS
══════════════════════════════════════════ */
const NosotrosSection = styled(Section)`
  background: #fff;
`;

const NosotrosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-top: 40px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const NosotrosCard = styled.div`
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(0,0,0,0.10);
  background: #f9fafb;
`;

const NosotrosImg = styled.img`
  width: 100%;
  aspect-ratio: 4/3;
  object-fit: cover;
  object-position: top;
  display: block;
`;

const NosotrosCaption = styled.div`
  padding: 16px 20px;
`;

const NosotrosCaptionTitle = styled.h3`
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 4px;
`;

const NosotrosCaptionDesc = styled.p`
  font-size: 13px;
  color: #6b7280;
  line-height: 1.55;
`;

/* ══════════════════════════════════════════
   RECONOCIMIENTOS
══════════════════════════════════════════ */
const ReconocimientosSection = styled(Section)`
  background: #f9fafb;
  text-align: center;
`;

const LogosRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 48px;
  margin-top: 40px;
  flex-wrap: wrap;
`;

const LogoCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px 32px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.07);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  min-width: 180px;
`;

const LogoImg = styled.img`
  height: 72px;
  width: auto;
  object-fit: contain;
`;

const LogoLabel = styled.p`
  font-size: 13px;
  color: #6b7280;
  line-height: 1.4;
  margin: 0;
`;

/* ══════════════════════════════════════════
   CTA FINAL
══════════════════════════════════════════ */
const CtaFinal = styled(Section)`
  background: #111827;
  color: white;
  text-align: center;
`;

const CtaTitle = styled.h2`
  font-size: 32px;
  font-weight: 900;
  margin-bottom: 16px;
  @media (max-width: 768px) { font-size: 24px; }
`;

const CtaSub = styled.p`
  font-size: 16px;
  opacity: 0.7;
  margin-bottom: 36px;
  max-width: 480px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.65;
`;

/* ══════════════════════════════════════════
   COMPONENTE PRINCIPAL
══════════════════════════════════════════ */


const SobreNosotros = () => (
  <main>

    {/* ── HERO ── */}
    <HeroWrapper>
      <HeroInner>
        <HeroText>
          <Tag>Kusina · Para restaurantes</Tag>
          <HeroTitle>
            Tú cocina.<br />
            <HeroAccent>Nosotros nos encargamos del resto.</HeroAccent>
          </HeroTitle>
          <HeroSub>
            Digitalizamos tu restaurante y te conectamos con más clientes — sin comisiones abusivas,
            sin contratos, sin complicaciones. Solo tú y tu cocina.
          </HeroSub>
          <WaButton href={WA_LINK} target="_blank" rel="noopener noreferrer">
            <WaIcon />
            Quiero saber más
          </WaButton>
          <div style={{
            marginTop: 24,
            marginBottom: 40,
            display: "inline-flex",
            alignItems: "center",
            gap: 12,
            background: "rgba(255,255,255,0.10)",
            border: "1px solid rgba(255,255,255,0.18)",
            borderRadius: 12,
            padding: "12px 20px",
          }}>
            <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.8, textTransform: "uppercase", opacity: 0.7, color: "white", whiteSpace: "nowrap" }}>
              Respaldados por
            </span>
            <div style={{ width: 1, height: 24, background: "rgba(255,255,255,0.25)" }} />
            <img src={camaraComercioLogo} alt="Cámara de Comercio de Ica" style={{ height: 72, width: "auto", objectFit: "contain" }} />
          </div>
        </HeroText>

        <HeroVideoBox>
          <VideoFrame
            src="https://www.youtube.com/embed/amAKrKmDZ1I?autoplay=1&mute=1&loop=1&playlist=amAKrKmDZ1I&controls=0&modestbranding=1&rel=0"
            title="Kusina - Supervisión de entregas"
            allow="autoplay; encrypted-media"
            allowFullScreen
          />
        </HeroVideoBox>
      </HeroInner>

    </HeroWrapper>

    {/* ── ¿QUÉ ES KUSINA? ── */}
    <QueSomos>
      <Container>
        <QueGrid>
          <div>
            <FounderImg src={fotoPublicidad} alt="Kusina presente en restaurantes aliados de Ica" />
          </div>
          <div>
            <Tag>¿Qué es Kusina?</Tag>
            <SectionHeading>Un aliado real para tu negocio de comida</SectionHeading>
            <Lead>
              Kusina es un sistema que digitaliza tu restaurante con <strong>Kusina P.O.S</strong> — gestión de pedidos,
              carta digital y control de tu negocio desde el celular — y te conecta con clientes
              mediante <strong>Kusina Delivery</strong>, una logística compartida que agrupa varios restaurantes
              en una sola ruta para reducir costos.
            </Lead>
            <Lead style={{ marginTop: 16 }}>
              Cobramos entre el <strong>18% y 20% de comisión</strong> — la mitad de lo que cobran otras apps.
              Porque creemos que el que más gana debe ser el que cocina.
            </Lead>
            <StatRow>
              <Stat>
                <StatNum>18–20%</StatNum>
                <StatLabel>Comisión Kusina</StatLabel>
              </Stat>
              <Stat>
                <StatNum accent>~35%</StatNum>
                <StatLabel>Comisión otras apps</StatLabel>
              </Stat>
              <Stat>
                <StatNum>S/ 0</StatNum>
                <StatLabel>Inversión inicial</StatLabel>
              </Stat>
            </StatRow>
          </div>
        </QueGrid>
      </Container>
    </QueSomos>

    {/* ── CÓMO FUNCIONA ── */}
    <ComoFunciona>
      <Container>
        <div style={{ textAlign: "center" }}>
          <Tag>Cómo funciona</Tag>
          <SectionHeading style={{ maxWidth: 560, margin: "0 auto 8px" }}>
            Tres pasos y ya estás vendiendo más
          </SectionHeading>
          <Lead style={{ margin: "0 auto" }}>Sin papeleo. Sin inversión. Con alguien de Kusina a tu lado.</Lead>
        </div>

        <StepsGrid>
          <StepCard>
            <StepNum>1</StepNum>
            <StepIcon>🧑‍🍳</StepIcon>
            <StepTitle>Te visitamos y configuramos todo</StepTitle>
            <StepText>
              Tu Pooler — un representante de Kusina — va a tu local, sube tu carta con fotos y precios,
              y configura el sistema P.O.S. Sin tecnicismos. En un solo día.
            </StepText>
          </StepCard>

          <StepCard>
            <StepNum accent>2</StepNum>
            <StepIcon>🧪</StepIcon>
            <StepTitle>Una semana de prueba sin compromiso</StepTitle>
            <StepText>
              Probamos juntos cómo llegan los pedidos, cómo funciona el recojo y la entrega.
              Si algo no está bien, lo ajustamos. Sin contratos, sin presión.
            </StepText>
          </StepCard>

          <StepCard>
            <StepNum>3</StepNum>
            <StepIcon>🛵</StepIcon>
            <StepTitle>Empiezas a recibir pedidos</StepTitle>
            <StepText>
              Los pedidos llegan por web o WhatsApp. Tu Pooler coordina el recojo. Kusina entrega.
              Tú solo te concentras en cocinar bien.
            </StepText>
          </StepCard>
        </StepsGrid>

        <NoteStrip>
          <strong>Sin contratos vinculantes.</strong> Sin inversión inicial. Con acompañamiento constante de tu Pooler.
          Puedes salir cuando quieras.
        </NoteStrip>
      </Container>
    </ComoFunciona>

    {/* ── ¿POR QUÉ KUSINA? ── */}
    <PorQue>
      <Container>
        <Tag>¿Por qué Kusina?</Tag>
        <SectionHeading>No somos una app más. Somos tu aliado.</SectionHeading>
        <Lead>
          Las grandes plataformas están diseñadas para cadenas y franquicias. Kusina está hecha
          para huariques, fondas y menús del día — los negocios que sostienen la gastronomía peruana de verdad.
        </Lead>

        <CompareGrid>
          <CompareCard good>
            <CompareLabel good>Con Kusina</CompareLabel>
            <CompareItem good>Comisión justa: 18–20% sobre el total de la venta</CompareItem>
            <CompareItem good>Logística compartida — menos costo por pedido</CompareItem>
            <CompareItem good>Pooler dedicado que conoce tu negocio</CompareItem>
            <CompareItem good>Enfocados en micro y pequeños restaurantes</CompareItem>
            <CompareItem good>Sin contrato de permanencia obligatoria</CompareItem>
            <CompareItem good>Carta digital configurada por nosotros</CompareItem>
          </CompareCard>

          <CompareCard>
            <CompareLabel>Otras apps</CompareLabel>
            <CompareItem>Comisiones de hasta 35% o más</CompareItem>
            <CompareItem>Un repartidor por pedido — costo elevado</CompareItem>
            <CompareItem>Soporte impersonal, sin atención directa</CompareItem>
            <CompareItem>Diseñadas para cadenas con volumen alto</CompareItem>
            <CompareItem>Contratos con permanencia y penalidades</CompareItem>
            <CompareItem>Tú solo tienes que armar la carta desde cero</CompareItem>
          </CompareCard>
        </CompareGrid>

      </Container>
    </PorQue>

    {/* ── COADMINISTRACIÓN ── */}
    <CoadminSection>
      <Container>
        <CoadminGrid>
          <CoadminVideo
            src="https://www.youtube.com/embed/3oM1Dredm88?autoplay=1&mute=1&loop=1&playlist=3oM1Dredm88&controls=0&modestbranding=1&rel=0"
            title="Kusina - Coadministración de restaurantes"
            allow="autoplay; encrypted-media"
            allowFullScreen
          />
          <div>
            <Tag>Gestión compartida</Tag>
            <SectionHeading>
              Tu negocio siempre en orden, aunque no estés
            </SectionHeading>
            <Lead>
              Kusina no solo recibe pedidos — te ayuda a administrar tu restaurante.
              Tu Pooler monitorea la operación en tiempo real para que nada se te escape.
            </Lead>
            <BulletList>
              <BulletItem>Carta digital siempre actualizada con precios y disponibilidad</BulletItem>
              <BulletItem>Seguimiento de cada orden desde que entra hasta que se entrega</BulletItem>
              <BulletItem>Reporte semanal de ventas y pedidos para que veas tu crecimiento</BulletItem>
              <BulletItem>Coordinación directa con el repartidor — tú solo cocinas</BulletItem>
            </BulletList>
          </div>
        </CoadminGrid>
      </Container>
    </CoadminSection>

    {/* ── POOLER ── */}
    <PoolerSection>
      <PoolerInner>
        <div>
          <Tag style={{ background: "rgba(255,255,255,0.15)", color: "white" }}>Tu Pooler</Tag>
          <SectionHeading style={{ color: "white" }}>
            No te dejamos solo. Nunca.
          </SectionHeading>
          <PoolerBenefits>
            <PoolerBenefit>
              <BenefitIcon>📋</BenefitIcon>
              <BenefitText>
                <BenefitTitle>Mantiene tu carta actualizada</BenefitTitle>
                <BenefitDesc>Precios, fotos, disponibilidad — tu Pooler lo gestiona por ti.</BenefitDesc>
              </BenefitText>
            </PoolerBenefit>
            <PoolerBenefit>
              <BenefitIcon>🛵</BenefitIcon>
              <BenefitText>
                <BenefitTitle>Coordina cada entrega</BenefitTitle>
                <BenefitDesc>Sabe cuándo recoge, cuándo llega. Tú no tienes que hacer seguimiento.</BenefitDesc>
              </BenefitText>
            </PoolerBenefit>
            <PoolerBenefit>
              <BenefitIcon>🔔</BenefitIcon>
              <BenefitText>
                <BenefitTitle>Te notifica cada pedido</BenefitTitle>
                <BenefitDesc>Llega al sistema y tú ya sabes qué preparar. Sin sorpresas.</BenefitDesc>
              </BenefitText>
            </PoolerBenefit>
            <PoolerBenefit>
              <BenefitIcon>💬</BenefitIcon>
              <BenefitText>
                <BenefitTitle>Siempre disponible por WhatsApp</BenefitTitle>
                <BenefitDesc>¿Algo no funciona? Tu Pooler responde. No hay mesa de ayuda impersonal.</BenefitDesc>
              </BenefitText>
            </PoolerBenefit>
          </PoolerBenefits>
        </div>

        <PoolerQuote>
          "Tu Pooler es tu aliado en campo. Se encarga de mantener tu carta activa, coordinar las
          entregas y avisarte cada pedido. Tú solo tienes que cocinar bien — de lo demás nos encargamos nosotros."
          <div style={{ marginTop: 20, fontSize: 14, opacity: 0.75, fontStyle: "normal" }}>
            — Equipo Kusina
          </div>
        </PoolerQuote>
      </PoolerInner>
    </PoolerSection>

    {/* ── TESTIMONIO ── */}
    <TestimonioSection>
      <Container style={{ textAlign: "center" }}>
        <Tag>Lo que dicen nuestros aliados</Tag>
        <SectionHeading style={{ maxWidth: 560, margin: "0 auto" }}>
          Restaurantes que ya venden más con Kusina
        </SectionHeading>

        <TestimonioCard>
          <Quote>
            "Antes perdía tiempo contestando pedidos por WhatsApp uno por uno.
            Ahora el sistema los organiza solo y el Pooler coordina la entrega.
            Yo solo me preocupo de que la comida salga bien."
          </Quote>
          <QuoteAuthor>Restaurante Aliado · Kusina</QuoteAuthor>
          <QuoteRole>Ica, Perú</QuoteRole>
        </TestimonioCard>

        <StatBanner>
          🍽️ Restaurantes en Ica ya confían en Kusina para crecer sin complicaciones
        </StatBanner>
      </Container>
    </TestimonioSection>

    {/* ── NOSOTROS ── */}
    <NosotrosSection>
      <Container>
        <div style={{ textAlign: "center" }}>
          <Tag>Nosotros</Tag>
          <SectionHeading style={{ maxWidth: 560, margin: "0 auto 8px" }}>
            Las personas detrás de Kusina
          </SectionHeading>
          <Lead style={{ margin: "0 auto" }}>
            Somos un equipo comprometido con el crecimiento de los restaurantes locales en el Perú.
          </Lead>
        </div>

        <NosotrosGrid>
          <NosotrosCard>
            <NosotrosImg src={fotoEmpleado1} alt="Equipo Kusina en la Cámara de Comercio de Ica" />
            <NosotrosCaption>
              <NosotrosCaptionTitle>Cámara de Comercio de Ica</NosotrosCaptionTitle>
              <NosotrosCaptionDesc>
                Reconocidos por la Cámara de Comercio de Ica como parte del ecosistema de innovación local.
              </NosotrosCaptionDesc>
            </NosotrosCaption>
          </NosotrosCard>

          <NosotrosCard>
            <NosotrosImg src={fotoEmpleado2} alt="Equipo Kusina en la Cámara de Comercio de Ica" />
            <NosotrosCaption>
              <NosotrosCaptionTitle>Cámara de Comercio de Ica</NosotrosCaptionTitle>
              <NosotrosCaptionDesc>
                Reconocidos por la Cámara de Comercio de Ica como parte del ecosistema de innovación local.
              </NosotrosCaptionDesc>
            </NosotrosCaption>
          </NosotrosCard>

          <NosotrosCard>
            <NosotrosImg src={fotoRepartidor} alt="Repartidor Kusina en operación" />
            <NosotrosCaption>
              <NosotrosCaptionTitle>Nuestros repartidores</NosotrosCaptionTitle>
              <NosotrosCaptionDesc>
                El equipo de entrega que conecta cada pedido con el cliente de manera rápida y confiable.
              </NosotrosCaptionDesc>
            </NosotrosCaption>
          </NosotrosCard>
        </NosotrosGrid>
      </Container>
    </NosotrosSection>

    {/* ── MISIÓN ── */}
    <MisionSection>
      <MisionInner>
        <MisionEyebrow>Nuestra misión</MisionEyebrow>
        <MisionQuote>
          "Asegurar que al menos <em>una de tus tres comidas</em> del día venga de un restaurante que conoces y confías."
        </MisionQuote>
        <MisionDesc>
          Cada pedido que procesa Kusina es una oportunidad para que un restaurante local crezca
          y para que una familia peruana coma bien. Eso es lo que nos mueve cada día.
        </MisionDesc>
      </MisionInner>
    </MisionSection>

    {/* ── RECONOCIMIENTOS ── */}
    <ReconocimientosSection>
      <Container>
        <Tag>Respaldo institucional</Tag>
        <SectionHeading style={{ maxWidth: 560, margin: "0 auto 8px" }}>
          Reconocidos y apoyados por quienes impulsan la innovación en el Perú
        </SectionHeading>
        <Lead style={{ margin: "0 auto" }}>
          Kusina fue seleccionada como ganadora del concurso <strong>Innovasuyu</strong> y cuenta con el apoyo de{" "}
          <strong>ProInnóvate</strong>, el programa de innovación del Ministerio de la Producción del Perú.
        </Lead>
        <LogosRow>
          <LogoCard>
            <LogoImg src={logoInnovasuyu} alt="InnovaSuyu - Ecosistemas Regionales de Innovación" />
            <LogoLabel>Ganadores del concurso<br /><strong>InnovaSuyu</strong></LogoLabel>
          </LogoCard>
          <LogoCard>
            <LogoImg src={logoProInnovate} alt="ProInnóvate - Ministerio de la Producción" />
            <LogoLabel>Con el apoyo de<br /><strong>ProInnóvate</strong></LogoLabel>
          </LogoCard>
        </LogosRow>
      </Container>
    </ReconocimientosSection>

    {/* ── CTA FINAL ── */}
    <CtaFinal>
      <Container>
        <CtaTitle>¿Listo para vender más sin complicarte?</CtaTitle>
        <CtaSub>
          Únete a la red de restaurantes que ya trabajan con Kusina.
          Sin inversión inicial, sin contratos, con alguien de tu lado.
        </CtaSub>
        <WaButton href={WA_LINK} target="_blank" rel="noopener noreferrer">
          <WaIcon />
          Únete a Kusina por WhatsApp
        </WaButton>
        <div style={{ marginTop: 24, fontSize: 13, opacity: 0.45, color: "white" }}>
          delivery restaurantes Ica · sistema para restaurantes Perú · Kusina App
        </div>
      </Container>
    </CtaFinal>

  </main>
);

export default SobreNosotros;
